import React, { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Collapse,
  CircularProgress,
  Paper,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { Patient } from '@/features/patients/types/patients'
import {
  Action,
  ActionStatus,
} from '@/features/patient-actions/types/patientActions'
import { usePatientDetailsQuery } from '@/features/patients'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import {
  ACTION_STATUS_LABELS,
  ACTION_GROUP_LABELS,
} from '@/apps/legacy/constants/patientConstants'
import { useUpdateActionStatusMutation } from '@/features/patient-actions'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import ActionCard from '@/apps/legacy/components/molecules/ActionCard'
import { PatientInfoDisplay } from '@/apps/legacy/components/molecules/PatientInfoDisplay'
import { LineTab } from '@/features/internal/patient-messaging'

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  )
}

// アクションタグリストコンポーネント
interface ActionTagsProps {
  actions: Action[]
}

const ActionTags: React.FC<ActionTagsProps> = ({ actions }) => {
  if (!actions || actions.length === 0) return null

  // グループごとにユニークなアクションを取得
  const uniqueGroups = Array.from(
    new Set(actions.map((action) => action.group))
  )

  return (
    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      {uniqueGroups.map((group) => (
        <Chip
          key={group}
          label={ACTION_GROUP_LABELS[group]}
          size="small"
          sx={{
            height: 18,
            backgroundColor: colorTokens.action.primaryBgLight,
            color: colorTokens.blue[600],
            border: `1px solid ${colorTokens.blue[600]}`,
            '& .MuiChip-label': {
              px: 0.75,
            },
          }}
        />
      ))}
    </Box>
  )
}

interface PatientDetailDrawerProps {
  patient: Patient
  onClose: () => void
}

const PatientDetailDrawer: React.FC<PatientDetailDrawerProps> = ({
  patient,
}) => {
  const [tabValue, setTabValue] = useState(0)
  const queryClient = useQueryClient()
  const { data, actionsByStatus, isLoading, error, refetch } =
    usePatientDetailsQuery(patient.id)
  const actions = data?.actions ?? []
  const visits = data?.visits ?? []

  // TanStack Query Mutation
  const updateActionStatusMutation = useUpdateActionStatusMutation()

  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>(
    {}
  )

  const toggleCardExpanded = useCallback((cardId: string) => {
    setExpandedCards((prev) => ({
      ...prev,
      [cardId]: !prev[cardId],
    }))
  }, [])

  const handleStatusUpdate = useCallback(
    async (actionId: string, newStatus: ActionStatus) => {
      try {
        await updateActionStatusMutation.mutateAsync({
          actionId,
          status: newStatus,
        })
        // ステータス更新後にキャッシュを無効化して再取得
        await queryClient.invalidateQueries({
          queryKey: queryKeys.patientManagement.patientDetails(patient.id),
        })
      } catch {
        // エラーハンドリングはコンポーネント層で処理
      }
    },
    [patient.id, queryClient, updateActionStatusMutation]
  )

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          エラーが発生しました
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {error?.message}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => refetch()}
          sx={{
            borderColor: colorTokens.blue[600],
            color: colorTokens.blue[600],
          }}
        >
          再試行
        </Button>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 患者情報ヘッダー */}
      <PatientInfoDisplay
        patient={{
          name: patient.name_kanji,
          nameKana: patient.name_kana,
          patientCode: patient.patient_code,
          lastVisitDate: patient.last_visit_date,
        }}
        variant="drawer"
      />

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        sx={{
          px: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            color: colorTokens.text.secondary,
            '&.Mui-selected': {
              color: colorTokens.text.primary,
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: colorTokens.blue[600],
            height: 3,
          },
        }}
      >
        <Tab label="アクション管理" />
        <Tab label="タイムライン" />
        <Tab label="LINE" />
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto', pl: 2, pr: 1, py: 2 }}>
        {/* アクション管理 */}
        <TabPanel value={tabValue} index={0}>
          {/* 保留中のアクション */}
          {actionsByStatus.pending.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: colorTokens.text.primary,
                }}
              >
                {ACTION_STATUS_LABELS.pending}
              </Typography>
              {actionsByStatus.pending.map((action) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  onStatusUpdate={(newStatus) =>
                    handleStatusUpdate(action.id, newStatus)
                  }
                  maxWidth="100%"
                  showGroupChip={true}
                />
              ))}
            </Box>
          )}

          {/* 計画済みのアクション */}
          {actionsByStatus.planned.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: colorTokens.text.primary,
                }}
              >
                {ACTION_STATUS_LABELS.planned}
              </Typography>
              {actionsByStatus.planned.map((action) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  onStatusUpdate={(newStatus) =>
                    handleStatusUpdate(action.id, newStatus)
                  }
                  maxWidth="100%"
                  showGroupChip={true}
                />
              ))}
            </Box>
          )}

          {/* 実施中のアクション */}
          {actionsByStatus.inProgress.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: colorTokens.text.primary,
                }}
              >
                {ACTION_STATUS_LABELS.in_progress}
              </Typography>
              {actionsByStatus.inProgress.map((action) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  onStatusUpdate={(newStatus) =>
                    handleStatusUpdate(action.id, newStatus)
                  }
                  maxWidth="100%"
                  showGroupChip={true}
                />
              ))}
            </Box>
          )}

          {/* 完了したアクション */}
          {actionsByStatus.done.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: colorTokens.text.primary,
                }}
              >
                {ACTION_STATUS_LABELS.done}
              </Typography>
              {actionsByStatus.done.map((action) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  onStatusUpdate={(newStatus) =>
                    handleStatusUpdate(action.id, newStatus)
                  }
                  maxWidth="100%"
                  showGroupChip={true}
                />
              ))}
            </Box>
          )}

          {/* 却下されたアクション */}
          {actionsByStatus.declined.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: colorTokens.text.secondary,
                }}
              >
                {ACTION_STATUS_LABELS.declined}
              </Typography>
              {actionsByStatus.declined.map((action) => (
                <ActionCard
                  key={action.id}
                  action={action}
                  onStatusUpdate={(newStatus) =>
                    handleStatusUpdate(action.id, newStatus)
                  }
                  maxWidth="100%"
                  showGroupChip={true}
                />
              ))}
            </Box>
          )}

          {/* アクションが1つもない場合 */}
          {actions.length === 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: colorTokens.background.subtle,
                border: `1px solid ${colorTokens.border.light}`,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: colorTokens.text.secondary,
                }}
              >
                アクションはありません
              </Typography>
            </Paper>
          )}
        </TabPanel>

        {/* タイムライン */}
        <TabPanel value={tabValue} index={1}>
          {visits && visits.length > 0 ? (
            visits.map((visit) => (
              <Card key={visit.id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="h6">
                          {visit.visit_date} - {visit.hospital.name}
                        </Typography>
                        <ActionTags actions={visit.actions} />
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {visit.prescription.groups
                          .slice(0, 2)
                          .map((group) =>
                            group.drugs.map((drug) => drug.name).join(', ')
                          )
                          .join(' / ')}
                        {visit.prescription.groups.length > 2 && ' 他...'}
                      </Typography>
                    </Box>
                    <IconButton onClick={() => toggleCardExpanded(visit.id)}>
                      {expandedCards[visit.id] ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </IconButton>
                  </Box>

                  <Collapse in={expandedCards[visit.id]}>
                    <Divider sx={{ my: 2 }} />

                    {/* 処方内容 */}
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        処方内容
                      </Typography>
                      {visit.prescription.groups.map((group, groupIndex) => (
                        <Box key={groupIndex} sx={{ mb: 1, pl: 2 }}>
                          {group.drugs.map((drug, drugIndex) => (
                            <Typography key={drugIndex} variant="body2">
                              {drug.name} {drug.quantity}
                              {drug.unit}
                            </Typography>
                          ))}
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ pl: 2 }}
                          >
                            {group.usage.name} {group.usage.days}日分
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    {/* SOAP記録 */}
                    {(visit.soap.subjectives.length > 0 ||
                      visit.soap.objectives.length > 0 ||
                      visit.soap.assessments.length > 0) && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          SOAP記録
                        </Typography>
                        {visit.soap.subjectives.length > 0 && (
                          <Box sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              S:
                            </Typography>
                            {visit.soap.subjectives.map((item, i) => (
                              <Typography
                                key={i}
                                variant="body2"
                                sx={{ pl: 2 }}
                              >
                                {item.content}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {visit.soap.objectives.length > 0 && (
                          <Box sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              O:
                            </Typography>
                            {visit.soap.objectives.map((item, i) => (
                              <Typography
                                key={i}
                                variant="body2"
                                sx={{ pl: 2 }}
                              >
                                {item.content}
                              </Typography>
                            ))}
                          </Box>
                        )}
                        {visit.soap.assessments.length > 0 && (
                          <Box sx={{ mb: 1, pl: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              A:
                            </Typography>
                            {visit.soap.assessments.map((item, i) => (
                              <Typography
                                key={i}
                                variant="body2"
                                sx={{ pl: 2 }}
                              >
                                {item.content}
                              </Typography>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            ))
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: colorTokens.background.subtle,
                border: `1px solid ${colorTokens.border.light}`,
                borderRadius: 1,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: colorTokens.text.secondary,
                }}
              >
                来院履歴はありません
              </Typography>
            </Paper>
          )}
        </TabPanel>

        {/* LINE */}
        <TabPanel value={tabValue} index={2}>
          <LineTab patientId={patient.id} />
        </TabPanel>
      </Box>
    </Box>
  )
}

export default PatientDetailDrawer
